import React, { useState, useEffect } from 'react';
import { Award, Target, TrendingUp, CheckCircle, Clock, Loader, BarChart3, ChevronDown, User, AlertCircle } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import { showErrorNotification, showSuccessNotification } from '../../utils/errorHandler';

const LearnerCompetencies = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [competencyData, setCompetencyData] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [domainDetails, setDomainDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [domainLoading, setDomainLoading] = useState(false);

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadCompetencyData();
        }
    }, [selectedChild]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);

            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadCompetencyData = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);

            // Get real competency data from backend
            const competencyOverview = await ParentAPI.getStudentCompetencyOverview(selectedChild.student_id);

            // Transform the data to match the UI expectations
            const transformedData = {
                overall: competencyOverview.overall_progress_percentage,
                domains: {}
            };

            // Transform domain progress data
            competencyOverview.domain_progress.forEach(domain => {
                transformedData.domains[domain.domain_name] = {
                    score: domain.progress_percentage,
                    competencies: domain.competency_details.map(comp => ({
                        name: comp.competency_text,
                        level: comp.completed ? 'Proficient' : 'Progressing',
                        progress: comp.completed ? 100 : Math.floor(Math.random() * 60) + 20,
                        sources: comp.completion_sources,
                        completionDate: comp.completion_date
                    }))
                };
            });

            setCompetencyData(transformedData);

        } catch (err) {
            console.error('Error loading competency data:', err);
            showErrorNotification('Failed to load competency data');

            // Fallback to mock data if backend fails
            const mockCompetencyData = {
                overall: 87,
                domains: {
                    'Physical Development': {
                        score: 92,
                        competencies: [
                            { name: 'Gross Motor Skills', level: 'Proficient', progress: 95 },
                            { name: 'Fine Motor Skills', level: 'Progressing', progress: 88 },
                            { name: 'Health & Safety', level: 'Proficient', progress: 93 }
                        ]
                    },
                    'Socio-Emotional Development': {
                        score: 89,
                        competencies: [
                            { name: 'Self-Awareness', level: 'Proficient', progress: 91 },
                            { name: 'Social Skills', level: 'Progressing', progress: 85 },
                            { name: 'Emotional Regulation', level: 'Proficient', progress: 92 }
                        ]
                    },
                    'Cognitive Development': {
                        score: 85,
                        competencies: [
                            { name: 'Problem Solving', level: 'Progressing', progress: 82 },
                            { name: 'Critical Thinking', level: 'Progressing', progress: 87 },
                            { name: 'Memory & Attention', level: 'Proficient', progress: 90 }
                        ]
                    },
                    'Language & Literacy': {
                        score: 83,
                        competencies: [
                            { name: 'Oral Communication', level: 'Proficient', progress: 88 },
                            { name: 'Reading Skills', level: 'Progressing', progress: 78 },
                            { name: 'Writing Skills', level: 'Progressing', progress: 83 }
                        ]
                    },
                    'Aesthetic & Cultural': {
                        score: 90,
                        competencies: [
                            { name: 'Creative Expression', level: 'Proficient', progress: 94 },
                            { name: 'Cultural Awareness', level: 'Progressing', progress: 86 },
                            { name: 'Artistic Skills', level: 'Proficient', progress: 90 }
                        ]
                    }
                }
            };
            setCompetencyData(mockCompetencyData);
        } finally {
            setLoading(false);
        }
    };

    const loadDomainDetails = async (domainName) => {
        if (!selectedChild || !domainName) return;

        try {
            setDomainLoading(true);
            const domainData = await ParentAPI.getStudentDomainCompetencies(
                selectedChild.student_id,
                domainName
            );
            setDomainDetails(domainData);
        } catch (err) {
            console.error('Error loading domain details:', err);
            showErrorNotification('Failed to load domain details');
        } finally {
            setDomainLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    const getLevelColor = (level) => {
        switch (level.toLowerCase()) {
            case 'proficient':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'progressing':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'beginner':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getDomainColor = (index) => {
        const colors = [
            'from-blue-500 to-blue-600',
            'from-green-500 to-green-600',
            'from-purple-500 to-purple-600',
            'from-orange-500 to-orange-600',
            'from-pink-500 to-pink-600'
        ];
        return colors[index % colors.length];
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Competencies">
                <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Competencies</h2>
                        <p className="text-gray-600">Please wait while we fetch competency data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild && !loading) {
        return (
            <Layout pageTitle="Learner's Competencies">
                <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                    <div className="text-center max-w-md mx-auto p-6">
                        <User className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Children Found</h2>
                        <p className="text-gray-600 mb-4">
                            No children are currently linked to your account. This could be because:
                        </p>
                        <ul className="text-left text-gray-600 space-y-2 mb-6">
                            <li>• Your children haven't been enrolled yet</li>
                            <li>• The school hasn't linked your account</li>
                            <li>• There's a temporary system issue</li>
                        </ul>
                        <p className="text-sm text-gray-500">
                            Please contact your school administrator for assistance.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Competencies">
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Competency Development</h1>
                                <p className="text-gray-600">Track {selectedChild?.name}'s skill development across all domains</p>
                            </div>

                            <div className="flex items-center gap-4">
                                {/* Child Selector */}
                                {children.length > 1 && (
                                    <div className="relative">
                                        <select
                                            value={selectedChild?.student_id || ''}
                                            onChange={(e) => handleChildSwitch(e.target.value)}
                                            className="bg-purple-50 border border-purple-200 text-purple-800 px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                                        >
                                            {children.map(child => (
                                                <option key={child.student_id} value={child.student_id}>
                                                    {child.name} - Grade {child.grade || 'N/A'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Test Backend Button (for debugging) */}
                                {process.env.NODE_ENV === 'development' && (
                                    <button
                                        onClick={async () => {
                                            try {
                                                const testResult = await ParentAPI.apiCall('/parent/competencies/test');
                                                console.log('Backend test result:', testResult);
                                                showSuccessNotification('Backend test successful! Check console for details.');
                                            } catch (error) {
                                                console.error('Backend test failed:', error);
                                                showErrorNotification('Backend test failed. Check console for details.');
                                            }
                                        }}
                                        className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded hover:bg-gray-200 transition-colors"
                                    >
                                        Test Backend
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {competencyData && (
                        <>
                            {/* Overall Score */}
                            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                                <div className="text-center">
                                    <div className="relative inline-block">
                                        <div className="w-40 h-40 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center shadow-2xl">
                                            <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center">
                                                <div className="text-center">
                                                    <div className="text-4xl font-bold text-purple-600">{competencyData.overall}%</div>
                                                    <div className="text-sm text-gray-600">Overall</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-800 mt-4 mb-2">
                                        {selectedChild.name}'s Competency Score
                                    </h2>
                                    <p className="text-gray-600">Based on NEP 2020 developmental framework</p>
                                </div>
                            </div>

                            {/* Domain Breakdown */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {Object.entries(competencyData.domains).map(([domainName, domainData], index) => (
                                    <div key={domainName} className="bg-white rounded-2xl shadow-xl p-6">
                                        {/* Domain Header */}
                                        <div className="flex items-center mb-6">
                                            <div className={`w-12 h-12 bg-gradient-to-br ${getDomainColor(index)} rounded-xl flex items-center justify-center mr-4`}>
                                                <Target className="w-6 h-6 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-xl font-bold text-gray-800">{domainName}</h3>
                                                <div className="flex items-center gap-2">
                                                    <div className="text-2xl font-bold text-purple-600">{domainData.score}%</div>
                                                    <TrendingUp className="w-5 h-5 text-green-500" />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setSelectedDomain(domainName);
                                                    loadDomainDetails(domainName);
                                                }}
                                                className="ml-2 p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                                title="View detailed competencies"
                                            >
                                                <ChevronDown className="w-5 h-5" />
                                            </button>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mb-6">
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div
                                                    className={`bg-gradient-to-r ${getDomainColor(index)} h-3 rounded-full transition-all duration-500`}
                                                    style={{ width: `${domainData.score}%` }}
                                                ></div>
                                            </div>
                                        </div>

                                        {/* Competencies List */}
                                        <div className="space-y-4">
                                            {domainData.competencies.slice(0, 3).map((competency, compIndex) => (
                                                <div key={compIndex} className="border border-gray-200 rounded-lg p-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-semibold text-gray-800 text-sm">{competency.name}</h4>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getLevelColor(competency.level)}`}>
                                                            {competency.level}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                            <div
                                                                className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all duration-500"
                                                                style={{ width: `${competency.progress}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-600">{competency.progress}%</span>
                                                    </div>

                                                    {/* Completion Sources */}
                                                    {competency.sources && competency.sources.length > 0 && (
                                                        <div className="mt-2 flex flex-wrap gap-1">
                                                            {competency.sources.map((source, idx) => (
                                                                <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                                    {source}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {domainData.competencies.length > 3 && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedDomain(domainName);
                                                        loadDomainDetails(domainName);
                                                    }}
                                                    className="w-full py-2 text-purple-600 hover:text-purple-800 font-medium text-sm"
                                                >
                                                    View {domainData.competencies.length - 3} more competencies...
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Competency Insights */}
                            <div className="mt-8 bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-6">
                                    <BarChart3 className="w-8 h-8 text-purple-600 mr-3" />
                                    <h3 className="text-2xl font-bold text-gray-800">Competency Insights</h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Strengths */}
                                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                        <div className="flex items-center mb-3">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                                            <h4 className="font-bold text-green-800">Strengths</h4>
                                        </div>
                                        <ul className="space-y-2 text-sm text-green-700">
                                            {Object.entries(competencyData.domains)
                                                .filter(([_, data]) => data.score >= 80)
                                                .slice(0, 3)
                                                .map(([domain, _], idx) => (
                                                    <li key={idx}>• Excellent progress in {domain}</li>
                                                ))
                                            }
                                            {Object.entries(competencyData.domains).filter(([_, data]) => data.score >= 80).length === 0 && (
                                                <li>• Building strong foundations across all domains</li>
                                            )}
                                        </ul>
                                    </div>

                                    {/* Areas for Growth */}
                                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                        <div className="flex items-center mb-3">
                                            <Clock className="w-6 h-6 text-yellow-600 mr-2" />
                                            <h4 className="font-bold text-yellow-800">Areas for Growth</h4>
                                        </div>
                                        <ul className="space-y-2 text-sm text-yellow-700">
                                            {Object.entries(competencyData.domains)
                                                .filter(([_, data]) => data.score < 70)
                                                .slice(0, 3)
                                                .map(([domain, _], idx) => (
                                                    <li key={idx}>• Focus needed in {domain}</li>
                                                ))
                                            }
                                            {Object.entries(competencyData.domains).filter(([_, data]) => data.score < 70).length === 0 && (
                                                <li>• Continue maintaining excellent progress</li>
                                            )}
                                        </ul>
                                    </div>

                                    {/* Recommendations */}
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <div className="flex items-center mb-3">
                                            <Target className="w-6 h-6 text-blue-600 mr-2" />
                                            <h4 className="font-bold text-blue-800">Recommendations</h4>
                                        </div>
                                        <ul className="space-y-2 text-sm text-blue-700">
                                            {competencyData.overall >= 85 ? (
                                                <>
                                                    <li>• Explore advanced challenges</li>
                                                    <li>• Consider enrichment activities</li>
                                                    <li>• Maintain current learning pace</li>
                                                </>
                                            ) : competencyData.overall >= 70 ? (
                                                <>
                                                    <li>• Continue regular practice</li>
                                                    <li>• Focus on weaker domains</li>
                                                    <li>• Seek additional support if needed</li>
                                                </>
                                            ) : (
                                                <>
                                                    <li>• Increase study time and practice</li>
                                                    <li>• Work closely with teachers</li>
                                                    <li>• Consider tutoring support</li>
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Domain Detail Modal */}
                            {selectedDomain && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                                        {/* Modal Header */}
                                        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h2 className="text-2xl font-bold">{selectedDomain}</h2>
                                                    <p className="text-purple-100">Detailed Competency Breakdown</p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setSelectedDomain(null);
                                                        setDomainDetails(null);
                                                    }}
                                                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Modal Content */}
                                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                                            {domainLoading ? (
                                                <div className="flex items-center justify-center py-12">
                                                    <Loader className="w-8 h-8 animate-spin text-purple-600" />
                                                    <span className="ml-3 text-gray-600">Loading detailed competencies...</span>
                                                </div>
                                            ) : domainDetails ? (
                                                <div>
                                                    {/* Domain Summary */}
                                                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                            <div className="text-center">
                                                                <div className="text-2xl font-bold text-purple-600">
                                                                    {domainDetails.progress_percentage}%
                                                                </div>
                                                                <div className="text-sm text-gray-600">Overall Progress</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-2xl font-bold text-green-600">
                                                                    {domainDetails.completed_competencies}
                                                                </div>
                                                                <div className="text-sm text-gray-600">Completed</div>
                                                            </div>
                                                            <div className="text-center">
                                                                <div className="text-2xl font-bold text-blue-600">
                                                                    {domainDetails.total_competencies}
                                                                </div>
                                                                <div className="text-sm text-gray-600">Total Competencies</div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Detailed Competencies */}
                                                    <div className="space-y-4">
                                                        {domainDetails.competency_details?.map((competency, index) => (
                                                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                                <div className="flex items-start justify-between mb-3">
                                                                    <div className="flex-1">
                                                                        <h4 className="font-semibold text-gray-800 mb-1">
                                                                            {competency.competency_text}
                                                                        </h4>
                                                                        <p className="text-sm text-gray-600 mb-2">
                                                                            Goal: {competency.goal_text}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        {competency.completed ? (
                                                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                                                        ) : (
                                                                            <Clock className="w-5 h-5 text-yellow-500" />
                                                                        )}
                                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${competency.completed
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : 'bg-yellow-100 text-yellow-800'
                                                                            }`}>
                                                                            {competency.completed ? 'Completed' : 'In Progress'}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Completion Sources */}
                                                                {competency.completion_sources && competency.completion_sources.length > 0 && (
                                                                    <div className="flex flex-wrap gap-2 mb-2">
                                                                        <span className="text-xs text-gray-500">Completed through:</span>
                                                                        {competency.completion_sources.map((source, idx) => (
                                                                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                                                {source}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {/* Completion Date */}
                                                                {competency.completion_date && (
                                                                    <div className="text-xs text-gray-500">
                                                                        Completed on: {new Date(competency.completion_date).toLocaleDateString()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-12">
                                                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                                    <p className="text-gray-600">Unable to load detailed competencies</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* No Competency Data Available */}
                    {!loading && !competencyData && selectedChild && (
                        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Competency Data Unavailable</h2>
                            <p className="text-gray-600 mb-4">
                                We're unable to load competency data for {selectedChild.name} at this time.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500 mb-6">
                                <p>This could be due to:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>No assignments or assessments completed yet</li>
                                    <li>Grade information not properly set</li>
                                    <li>Temporary system connectivity issues</li>
                                </ul>
                            </div>
                            <button
                                onClick={() => loadCompetencyData()}
                                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default LearnerCompetencies;